import { Link } from 'react-router-dom'
import {
  ArrowLeftIcon,
  PlayIcon,
  StarIcon,
  VideoCameraIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolid } from '@heroicons/react/24/solid'
import ZaloJoinPanel from './ZaloJoinPanel'
import {
  ClassroomSessionItem,
  formatDateTimeRange,
  formatDateVi,
  isLiveMethod,
  resolveMeetLink,
  sessionDisplayName,
} from './types'

interface CourseDetailHeaderProps {
  courseId: number
  courseName: string
  method: string
  statusLabel?: string
  classroomName?: string | null
  startDate?: string | null
  nextSession: ClassroomSessionItem | null
  classroomMeetLink?: string | null
  hasReview: boolean
  myRate: number | null
  onOpenReview: () => void
  /** Show Zalo panel on the right of title when student has a classroom */
  zaloLink?: string | null
  showZaloPanel?: boolean
  completedCount?: number
  totalCount?: number
}

function methodBadge(method: string): string {
  if (method === 'VIDEO') return 'Tự học qua Video'
  if (method === 'OFFLINE') return 'Học trực tiếp (Offline)'
  if (method === 'HYBRID') return 'Học Hybrid'
  return 'Học trực tuyến (Online)'
}

const CourseDetailHeader = ({
  courseId,
  courseName,
  method,
  statusLabel,
  classroomName,
  startDate,
  nextSession,
  classroomMeetLink,
  hasReview,
  myRate,
  onOpenReview,
  zaloLink,
  showZaloPanel = false,
  completedCount = 0,
  totalCount = 0,
}: CourseDetailHeaderProps) => {
  const meetLink = resolveMeetLink(nextSession, classroomMeetLink)
  const showMeeting = isLiveMethod(method) && !!meetLink

  const ctaButtons = (
    <>
      {showMeeting && (
        <a
          href={meetLink!}
          target="_blank"
          rel="noreferrer"
          className="btn-primary-soft px-5 font-bold"
        >
          <VideoCameraIcon className="h-5 w-5 mr-2" />
          Vào Meeting
        </a>
      )}
      <Link
        to={`/student/learn/${courseId}`}
        className={`inline-flex h-10 items-center px-5 font-semibold rounded-lg ${
          showMeeting || (isLiveMethod(method) && nextSession)
            ? 'btn-secondary'
            : 'btn-primary-soft'
        }`}
      >
        <PlayIcon className="h-5 w-5 mr-2" />
        Vào học (Tài liệu/video)
      </Link>
      <button
        type="button"
        onClick={onOpenReview}
        className={`inline-flex h-10 items-center px-5 font-semibold rounded-lg border transition ${
          hasReview
            ? 'bg-m2-primary/15 hover:bg-m2-primary/25 text-m2-primary border-m2-primary/40'
            : 'bg-m2-surface-low hover:bg-m2-surface-high text-m2-on-surface border-m2-outline-variant'
        }`}
      >
        {hasReview ? (
          <StarSolid className="h-5 w-5 mr-2 text-m2-primary" />
        ) : (
          <StarIcon className="h-5 w-5 mr-2" />
        )}
        {hasReview
          ? myRate
            ? `Đánh giá của tôi (${myRate}/5)`
            : 'Đánh giá của tôi'
          : 'Đánh giá'}
      </button>
    </>
  )

  return (
    <div className="bg-m2-surface-lowest border-b border-m2-outline-variant">
      <div className="relative eh-container py-8 sm:py-10">
        <Link
          to="/student/my-courses"
          className="inline-flex items-center gap-1.5 text-sm text-m2-on-surface-variant hover:text-m2-primary mb-4 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Khóa của tôi
        </Link>

        <div
          className={`grid grid-cols-1 gap-6 items-stretch ${
            showZaloPanel ? 'lg:grid-cols-[1fr_minmax(240px,300px)]' : ''
          }`}
        >
          {/* Cột trái: tên khóa + CTA */}
          <div className="min-w-0 flex flex-col">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-m2-primary/20 text-m2-primary border border-m2-primary/30">
                {methodBadge(method)}
              </span>
              {statusLabel && (
                <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-m2-surface-container text-m2-on-surface border border-m2-outline-variant">
                  {statusLabel}
                </span>
              )}
            </div>

            <h1 className="text-3xl sm:text-4xl font-extrabold text-m2-on-surface mb-2 leading-tight">
              {courseName}
            </h1>

            {(classroomName || startDate) && (
              <p className="text-m2-on-surface-variant mb-6">
                {classroomName && <span>Lớp: {classroomName}</span>}
                {classroomName && startDate && <span> · </span>}
                {startDate && <span>Khai giảng: {formatDateVi(startDate)}</span>}
              </p>
            )}

            {isLiveMethod(method) && nextSession ? (
              <div className="flex flex-1 flex-col rounded-2xl border border-m2-outline-variant bg-m2-surface-container p-4 sm:p-5">
                <div className="flex items-start gap-3 mb-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-m2-primary/15 text-m2-primary">
                    <CalendarIcon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm text-m2-on-surface-variant">Buổi học gần nhất</p>
                    <p className="font-semibold text-m2-on-surface">
                      {sessionDisplayName(nextSession)}
                    </p>
                    <p className="text-sm text-m2-on-surface-variant mt-0.5">
                      {formatDateTimeRange(nextSession.startTime, nextSession.endTime)}
                    </p>
                  </div>
                </div>
                <div className="mt-auto flex flex-wrap items-center gap-3">{ctaButtons}</div>
              </div>
            ) : (
              <div className="mt-auto flex flex-wrap items-center gap-3">{ctaButtons}</div>
            )}
          </div>

          {/* Cột phải: QR Zalo ngang với tên khóa */}
          {showZaloPanel && (
            <div className="w-full lg:w-[300px]">
              <ZaloJoinPanel
                zaloLink={zaloLink}
                completedCount={completedCount}
                totalCount={totalCount}
                compact
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CourseDetailHeader
